import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum ArkFoodsPurchaseOrderLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit - can be rounded up to 4 decimal places
  lineTotal = 'lineTotal', // mapped in to account for rounding errors
}

export const arkFoodsPurchaseOrderAddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  // Single-string address from before the split fields; existing steps still map it.
  address: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
})
export type ArkFoodsPurchaseOrderAddressInfo = yup.InferType<
  typeof arkFoodsPurchaseOrderAddressInfoSchema
>

export const arkFoodsPurchaseOrderFileDataSchema = yup.object().shape({
  issuer: arkFoodsPurchaseOrderAddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  date: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  organizationLogoOverride: yup.string().nullable().optional(),
  destination: arkFoodsPurchaseOrderAddressInfoSchema,
  vendor: arkFoodsPurchaseOrderAddressInfoSchema,
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  notes: yup.string().nullable().optional(),
})

export type ArkFoodsPurchaseOrderFileData = yup.InferType<
  typeof arkFoodsPurchaseOrderFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<ArkFoodsPurchaseOrderFileData>

export const arkFoodsPurchaseOrderFileMappings: RequiredFileData = {
  issuer: {
    attn: 'c93b31c5-11e6-4d4a-9155-2d0b5f4a45b7',
    companyName: 'b4fae22e-d47e-4b11-a547-67a9632c96c8',
    addressLine1: '5f1aa652-0d2f-4f07-a82d-e7d0fefc0666',
    addressLine2: '1f9c053a-cd58-471e-b706-2d52db95a126',
    address: '9aef6d37-3a17-4c8d-8c6c-cba41e3c93d5',
    city: 'dfd60ac3-ba13-44e8-a7fc-b546f21e8c3c',
    state: '7d48ebfe-053b-43f1-af35-db22df594511',
    zipCode: '32c094c7-e771-407a-8293-edabed2d5411',
    country: 'ced65686-b3b7-41d4-8767-59fd1c58f8bb',
    phoneNumber: '42dc0b7b-b93d-4f5e-8717-5b3a7d27bfa9',
    emailAddress: '74b83e12-7c56-45a2-b52c-25c26cc74825',
  },
  orderNumber: 'b7a1f657-09a4-4a9a-9a6f-97c9a8b83f7f',
  paymentTerms: '9b7f7d8f-258c-4fa2-a681-cc0a1a37c7d1',
  freightTerms: '77c64d1f-16ee-4b39-8e69-52d7ac3ab53e',
  organizationLogoOverride: 'd3a9225f-4d97-4d9e-8c9a-fb3e7de7ad0a',
  date: '1a27c9a8-57db-46b5-8e14-31aefb4c822b',
  destination: {
    attn: 'a09eab4c-8046-4ab3-a5cb-7b1d3c3ff176',
    companyName: 'b2c9d81f-6b93-4d8b-b8c8-d8cf74873cb5',
    addressLine1: 'fcdb76c5-040b-44fe-8710-82a8e4de266a',
    addressLine2: '35263277-c52d-48b6-8902-8a3dd13bb429',
    address: '65f47a51-bba7-4e04-bc91-7194e508ac81',
    city: '35a0dd24-7269-417c-9dad-a452ee90f5de',
    state: 'd458a28b-803c-4c5e-a9f0-b390dc5ba404',
    zipCode: '52852c29-d71f-4c52-8c0c-f201d12614af',
    country: 'c1ffda00-e3c1-4f6c-a432-7e20597e9fcd',
    phoneNumber: 'e9bc9b7d-1eb8-40f2-8211-53e64e35a6f8',
    emailAddress: 'fa38b0d3-56e7-4f2b-bd6a-3c04b5f48a89',
  },
  vendor: {
    attn: 'bca04d92-f6e8-48ea-b80a-7323e56f497f',
    companyName: 'ce48d221-bcc5-4db6-8c35-2b69e7b1e7f4',
    addressLine1: 'c64a4ea7-73cc-45cb-85c8-8fabe57416ff',
    addressLine2: 'f05a4b6e-e3c0-4184-b83c-391d2be3a0dd',
    address: '2c1c6d63-ff8d-4eb2-bb7b-0e7d6dfe4791',
    city: 'b88e8b24-a6af-462d-869f-aec8d3c77825',
    state: '5fad1ca0-5b61-42cc-8a3b-57d83c21a6ec',
    zipCode: '21146bfe-7f6c-4774-a77a-e7ed0fa26d68',
    country: '8cc5ff67-6c12-4f93-b921-1ad75d4a0e34',
    phoneNumber: '7b8c4f12-7a2d-4d72-b47b-f7a10a2f6f24',
    emailAddress: '91cccb17-3b9d-4bb1-9111-57f03bda3b52',
  },
  items: [
    {
      itemNumber: null,
      itemName: 'ba9a4218-6b64-4b92-9d2c-d7bb70b66c8c',
      description: 'f83a48b2-68ff-4b2c-9585-8a7a3d2370cf',
      itemQty: 'e7a1d4c6-4689-41db-9545-0c84927d13f8',
      unitPrice: '3e38d7ad-0d3c-47ed-84f8-5e0b5c6b7c3a',
      lineTotal: '4af7c9fa-59ef-4f42-8f68-2a5cfb4a54e3',
    },
  ],
  notes: 'd492421a-72e3-476e-880f-6c5978631264',
}
